import React, { useState, useCallback, useMemo } from 'react';
import { useApiClient, useFetch, useFetchImmutable } from '@marageti/z4-lib';
import { useSnackbar, closeSnackbar } from 'notistack';
import { Button } from '@mui/material';
import { TeamMember } from '@marageti/z4-sdk/lib/people';
import { SearchResult } from '@marageti/z4-sdk/lib/entity';
import {
  Ticket,
  CreateTicketRequest,
  UpdateTicketDetailsRequest,
} from '@marageti/z4-sdk/lib/tickets';
import { TripRequest } from '@marageti/z4-sdk/lib/travel';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { useAuthStore } from '../store';
import {
  teamMemberSearchRequest,
  formatTeamMembersAsOptions,
  ANY_ASSIGNEE,
  AssigneeOption,
} from '../utils/team-members';

interface UseReminderActionsOptions {
  /** Callback to refresh the list after actions */
  refetch: () => void;
  /** If provided, new reminders will be associated with this TR */
  tripRequestId?: string;
  /** If provided, new reminders are associated with this traveler. */
  travelerId?: string;
  /** Exclude the "All" option from the assignees dropdown (for summary cards) */
  excludeAllAssignee?: boolean;
}

const useReminderActions = ({
  refetch,
  tripRequestId = '',
  travelerId = '',
  excludeAllAssignee = false,
}: UseReminderActionsOptions) => {
  const apiClient = useApiClient();
  const { enqueueSnackbar } = useSnackbar();
  const { loggedInTeamMember } = useAuthStore();

  // Determine if we have a trip request context (from a TR page)
  const hasTripRequestContext = !!tripRequestId;

  // Fetch team members for assignee dropdown
  const { data: teamMembers } = useFetch<SearchResult<TeamMember>>(
    apiClient.peopleClient.postTeamMemberSearch,
    [teamMemberSearchRequest],
    true,
  );

  // Fetch country codes for formatting trip request options
  const { data: countryCodes } = useFetchImmutable<CountryCodeMap>(
    apiClient.standardsClient.getCountryCodes,
    [],
    true,
  );

  // Fetch trip requests for the traveler (only when no TR context and we have a traveler ID)
  const {
    data: tripRequestsData,
    isLoading: isLoadingTripRequests,
  } = useFetch<SearchResult<TripRequest>>(
    apiClient.tripRequestsClient.postTripRequestSearches,
    [{
      filterBy: [{ field: 'travelers.id', value: [travelerId] }],
      sortBy: [{ field: 'createdAt', order: 'desc' }],
      pagination: { limit: 100, page: 1 },
    }],
    !hasTripRequestContext && !!travelerId,
  );

  const tripRequests = tripRequestsData?.hits;

  // Format team members as options
  const teamMembersAsOptions = useMemo<AssigneeOption[]>(
    () => formatTeamMembersAsOptions(teamMembers, excludeAllAssignee ? [] : [ANY_ASSIGNEE]),
    [teamMembers, excludeAllAssignee],
  );

  // Modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState<Ticket | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  // Action loading states
  const [reschedulingId, setReschedulingId] = useState<string | undefined>();
  const [togglingCompleteId, setTogglingCompleteId] = useState<string | undefined>();

  // ============ Actions ============

  const edit = useCallback((ticket: Ticket) => {
    setSelectedReminder(ticket);
    setIsCreatingNew(false);
    setEditModalOpen(true);
  }, []);

  const duplicate = useCallback((ticket: Ticket) => {
    setSelectedReminder(ticket);
    setIsCreatingNew(true);
    setEditModalOpen(true);
  }, []);

  const create = useCallback(() => {
    setSelectedReminder(null);
    setIsCreatingNew(true);
    setEditModalOpen(true);
  }, []);

  const reschedule = useCallback((ticket: Ticket) => {
    setSelectedReminder(ticket);
    setRescheduleModalOpen(true);
  }, []);

  const toggleComplete = useCallback(
    async (ticket: Ticket) => {
      setTogglingCompleteId(ticket.id);
      try {
        if (ticket.status === 'PENDING') {
          await apiClient.ticketsClient.updateTicketStatus(ticket.id, { status: 'COMPLETED' });
          enqueueSnackbar('Reminder completed', { variant: 'success' });
        } else {
          await apiClient.ticketsClient.updateTicketStatus(ticket.id, { status: 'PENDING' });
          enqueueSnackbar('Reminder reopened', { variant: 'success' });
        }
        await refetch();
      } catch (err) {
        enqueueSnackbar('Failed to update reminder', { variant: 'default' });
      } finally {
        setTogglingCompleteId(undefined);
      }
    },
    [apiClient, enqueueSnackbar, refetch],
  );

  const dismiss = useCallback(
    async (ticket: Ticket) => {
      const previousStatus = ticket.status;
      setTogglingCompleteId(ticket.id);

      try {
        await apiClient.ticketsClient.updateTicketStatus(ticket.id, { status: 'CANCELED' });

        const snackbarKey = enqueueSnackbar('Reminder Dismissed', {
          variant: 'success',
          action: (
            <Button
              variant="text"
              onClick={async () => {
                closeSnackbar(snackbarKey);
                try {
                  await apiClient.ticketsClient.updateTicketStatus(ticket.id, { status: previousStatus });
                  enqueueSnackbar('Reminder restored.', { variant: 'success' });
                  refetch();
                } catch (undoErr) {
                  enqueueSnackbar('Failed to undo.', { variant: 'default' });
                }
              }}
            >
              Undo
            </Button>
          ),
        });

        await refetch();
      } catch (err) {
        enqueueSnackbar('Failed to dismiss reminder', { variant: 'default' });
      } finally {
        setTogglingCompleteId(undefined);
      }
    },
    [apiClient, enqueueSnackbar, refetch],
  );

  // ============ Modal Handlers ============

  const handleEditModalClose = useCallback(() => {
    setEditModalOpen(false);
    setSelectedReminder(null);
    setIsCreatingNew(false);
  }, []);

  const handleEditModalSave = useCallback(
    async (
      data: CreateTicketRequest | UpdateTicketDetailsRequest,
      selectedTripRequestId?: string,
      selectedTravelerId?: string,
    ) => {
      try {
        if (isCreatingNew) {
          const createData = data as CreateTicketRequest;
          // Use selectedTripRequestId from modal if provided (from traveler profile or TR Reminders page)
          // Otherwise fall back to selectedReminder's TR (duplicate) or tripRequestId (from context)
          const finalTripRequestId = selectedTripRequestId
            || selectedReminder?.tripRequestId
            || tripRequestId;

          // Get traveler ID from the input selection (TR Reminders page) or from context
          let finalTravelerId = selectedTravelerId
            || selectedReminder?.travelerId
            || travelerId;
          if (!finalTravelerId && selectedTripRequestId && tripRequests) {
            const selectedTR = tripRequests.find((tr) => tr.id === selectedTripRequestId);
            if (selectedTR && selectedTR.travelers?.[0]?.id) {
              finalTravelerId = selectedTR.travelers[0].id;
            }
          }

          await apiClient.ticketsClient.createTicket({
            ...createData,
            ticketType: 'REMINDER',
            travelerId: finalTravelerId,
            tripRequestId: finalTripRequestId,
            tripMatchId: selectedReminder?.tripMatchId || '',
            partnerId: selectedReminder?.partnerId || '',
            status: selectedReminder?.status || 'PENDING',
          });
          enqueueSnackbar('Reminder created.', { variant: 'success' });
        } else if (selectedReminder) {
          const updateData = data as UpdateTicketDetailsRequest;
          await apiClient.ticketsClient.updateTicket(selectedReminder.id, {
            title: updateData.title,
            description: updateData.description,
            assignees: updateData.assignees,
            dueAt: updateData.dueAt,
          });
          enqueueSnackbar('Reminder updated.', { variant: 'success' });
        }
        handleEditModalClose();
        refetch();
      } catch (err) {
        enqueueSnackbar('Failed to save reminder.', { variant: 'default' });
      }
    },
    [
      apiClient,
      enqueueSnackbar,
      handleEditModalClose,
      isCreatingNew,
      refetch,
      selectedReminder,
      travelerId,
      tripRequestId,
      tripRequests,
    ],
  );

  const handleRescheduleModalClose = useCallback(() => {
    setRescheduleModalOpen(false);
    setSelectedReminder(null);
  }, []);

  const handleRescheduleModalSave = useCallback(
    async (newDueAt: string, previousDueAt: string) => {
      if (!selectedReminder) return;

      const ticketId = selectedReminder.id;
      const ticketData = {
        title: selectedReminder.title,
        description: selectedReminder.description,
        assignees: selectedReminder.assignees,
      };

      // Close modal immediately and show spinner on button
      handleRescheduleModalClose();
      setReschedulingId(ticketId);

      try {
        await apiClient.ticketsClient.updateTicket(ticketId, {
          ...ticketData,
          dueAt: newDueAt,
        });

        const snackbarKey = enqueueSnackbar('Reminder rescheduled.', {
          variant: 'success',
          action: (
            <Button
              variant="text"
              onClick={async () => {
                closeSnackbar(snackbarKey);
                try {
                  await apiClient.ticketsClient.updateTicket(ticketId, {
                    ...ticketData,
                    dueAt: previousDueAt,
                  });
                  enqueueSnackbar('Reminder date restored.', { variant: 'success' });
                  refetch();
                } catch (undoErr) {
                  enqueueSnackbar('Failed to undo.', { variant: 'default' });
                }
              }}
            >
              Undo
            </Button>
          ),
        });

        await refetch();
      } catch (err) {
        enqueueSnackbar('Failed to reschedule reminder.', { variant: 'default' });
      } finally {
        setReschedulingId(undefined);
      }
    },
    [apiClient, enqueueSnackbar, handleRescheduleModalClose, refetch, selectedReminder],
  );

  // ============ Default Values for New Reminder ============

  const newReminderDefaultValues = useMemo(() => ({
    assignedTo: loggedInTeamMember?.id || '',
  }), [loggedInTeamMember]);

  // ============ Modal Props ============

  // Get the trip request ID to pre-select when duplicating a reminder
  const duplicateTripRequestId = isCreatingNew && selectedReminder
    ? selectedReminder.tripRequestId
    : undefined;

  // Determine if we need traveler selection (from TR Reminders page)
  const needsTravelerSelection = !hasTripRequestContext && !travelerId;

  const editModalProps = useMemo(() => ({
    open: editModalOpen,
    onClose: handleEditModalClose,
    onSave: handleEditModalSave,
    ticket: isCreatingNew ? undefined : selectedReminder || undefined,
    // eslint-disable-next-line no-nested-ternary
    defaultValues: isCreatingNew
      ? (selectedReminder
        ? {
          title: selectedReminder.title,
          description: selectedReminder.description,
          dueAt: selectedReminder.dueAt,
          assignedTo: selectedReminder.assignees?.[0]?.id,
        }
        : newReminderDefaultValues)
      : undefined,
    assignees: teamMembersAsOptions,
    isNew: isCreatingNew,
    tripRequests,
    hasTripRequestContext,
    countryCodes,
    isLoadingTripRequests: !hasTripRequestContext && !!travelerId && isLoadingTripRequests,
    duplicateTripRequestId,
    needsTravelerSelection: isCreatingNew && needsTravelerSelection,
  }), [
    countryCodes,
    duplicateTripRequestId,
    editModalOpen,
    handleEditModalClose,
    handleEditModalSave,
    hasTripRequestContext,
    isCreatingNew,
    isLoadingTripRequests,
    needsTravelerSelection,
    newReminderDefaultValues,
    selectedReminder,
    teamMembersAsOptions,
    travelerId,
    tripRequests,
  ]);

  const rescheduleModalProps = useMemo(() => ({
    open: rescheduleModalOpen,
    onClose: handleRescheduleModalClose,
    onSave: handleRescheduleModalSave,
    currentDueAt: selectedReminder?.dueAt,
  }), [
    handleRescheduleModalClose,
    handleRescheduleModalSave,
    rescheduleModalOpen,
    selectedReminder?.dueAt,
  ]);

  return {
    // Actions
    edit,
    duplicate,
    create,
    reschedule,
    toggleComplete,
    dismiss,

    // Loading states
    reschedulingId,
    togglingCompleteId,

    // Selected reminder (for UI purposes)
    selectedReminder,

    // Modal props (spread these directly to the modals)
    editModalProps,
    rescheduleModalProps,

    // Assignees (for additional UI needs)
    assignees: teamMembersAsOptions,
  };
};

export default useReminderActions;
